import { DataSource, FiltrosModel } from '@/datasources/consulte-grid';
import { FotoPerfilModel } from '@/models/academico/estudantes/fotoestudante.model';
import {
  EnderecoEscolaParaLocalizar,
  EscolaModel,
  LocalizacaoEnderecoEscolaModel,
} from '@/models/administracao/escola.model';
import { PainelProfessores } from '@/models/administracao/painelprofessore.model';
import { RegisterOutput } from '@/models/register-output';
import TextoValor from '@/models/texto-valor';
import api from '@/services/axios/api';
import DadosLocais from '@/services/helper/storage';
import { obterExtensao } from '@/services/helper/upload-validacao.util';
import { useStore } from '@/store';
import { AxiosResponse } from 'axios';
import toastService from '../globals/toast.service';
import sessionService from '../helper/session.service';
const dadosLocais = DadosLocais.getInstance();

class EscolaService {
  codigoUsuario: number = -1;
  codigoGrupoUsuario: number = -1;
  codigoEscola: string | null = '';
  codigoProfessor: string | null = '';
  ano: string | null = '';
  semestre: string | null = '';

  constructor() {
    this.initialize();
  }

  initialize() {
    if (!sessionService.IsUsuarioSuporteOuDesenvolvimento()) {
      this.codigoUsuario = dadosLocais.getUserData().id;
      this.codigoGrupoUsuario = parseInt(dadosLocais.getCodigoGrupoUsuario() ?? '0');
    } else {
      this.codigoUsuario = -1;
      this.codigoGrupoUsuario = -1;
    }
    this.codigoEscola = useStore().getters.getCodigoEscola;
    this.ano = useStore().getters.getAno;
    this.semestre = useStore().getters.getSemestre;
    this.codigoProfessor = useStore().getters.getCodigoProfessor;
  }

  dadosIndexPaginado(
    pagina: number,
    itensPorPagina: number,
    filters: FiltrosModel[],
    sortDataField?: string,
  ): Promise<AxiosResponse<DataSource<EscolaModel>>> {
    this.initialize();
    return api.post<DataSource<EscolaModel>>(
      `/Escola/RetornoPaginado/${this.codigoUsuario}/${this.codigoGrupoUsuario}`,
      {
        rowsPerPage: itensPorPagina,
        pageNumber: pagina,
        filters: filters,
        sortDataField: sortDataField,
      },
    );
  }

  inserirAtualizar(escola: EscolaModel): Promise<AxiosResponse<RegisterOutput>> {
    const result = api.post('/Escola', escola);
    return result;
  }

  remover(id: number): Promise<AxiosResponse> {
    return api.delete('/Escola/' + id);
  }

  async atualizar(escola: EscolaModel): Promise<any> {
    try {
      await api.patch(`/Escola`, escola);
      toastService.alertaSucesso('Operação realizada com sucesso!');
      return true;
    } catch (exception: any) {
      if (exception.response && exception.response.data) {
        return exception.response.data;
      }

      return null;
    }
  }

  /**
   * Latitude/longitude do endereço da escola (Google Geocoding pela API — a chave fica no backend).
   * Só consulta, não grava: o ponto vai para o banco no `atualizar` do cadastro.
   */
  async localizarEndereco(
    endereco: EnderecoEscolaParaLocalizar,
  ): Promise<LocalizacaoEnderecoEscolaModel> {
    const result = await api.post<LocalizacaoEnderecoEscolaModel>(
      '/Escola/LocalizarEndereco',
      endereco,
    );

    return result.data ?? {};
  }

  async get(id: number): Promise<EscolaModel> {
    const result = await api.get<EscolaModel>(`/Escola/${id.toString()}`);

    const escolaModel = new EscolaModel();

    if (result.data) {
      Object.assign(escolaModel, result.data);
    }

    return escolaModel;
  }

  /** Nomes que a escola já teve, com o período de cada um (o ".." do cadastro da escola). */
  async listarHistoricoNomes(codigoEscola: number): Promise<HistoricoNomeEscola[]> {
    const result = await api.get<HistoricoNomeEscola[]>(`/Escola/${codigoEscola}/HistoricoNomes`);
    return Array.isArray(result.data) ? result.data : [];
  }

  /** Inclui (codigo = 0) ou altera um nome do histórico. Datas no formato yyyy-MM-dd. */
  async salvarHistoricoNome(codigoEscola: number, dados: HistoricoNomeEscola): Promise<void> {
    await api.post(`/Escola/${codigoEscola}/HistoricoNomes`, { ...dados });
  }

  async excluirHistoricoNome(codigoEscola: number, codigo: number): Promise<void> {
    await api.delete(`/Escola/${codigoEscola}/HistoricoNomes/${codigo}`);
  }

  async listarPainelProfessores(parteNome: String): Promise<PainelProfessores[]> {
    this.initialize();
    const response = await api.get(
      `/Escola/PainelProfessores/${this.codigoEscola}/${this.ano}/${parteNome}`,
    );
    return response.data;
  }

  async listarProfessores(
    codigoEnsinoSelecionado: number = 0,
    codigoTurmaSelecionado: number = 0,
  ): Promise<PainelProfessores[]> {
    this.initialize();
    const response = await api.get(
      `/Escola/Professores/${this.codigoEscola}/${this.ano}/${codigoEnsinoSelecionado}/${codigoTurmaSelecionado}`,
    );
    return response.data;
  }

  /**
   * Turmas e disciplinas do professor no painel dele. O semestre vai junto porque turma de ensino
   * semestral só existe no semestre dela — turma anual aparece sempre (RegraSemestreTurma).
   */
  async listarDadosProfessor(codigoProfessorSelecionado: number = 0): Promise<PainelProfessores[]> {
    this.initialize();
    const codigoProfessor =
      codigoProfessorSelecionado == 0 ? this.codigoProfessor : codigoProfessorSelecionado;
    const response = await api.get(
      `/Escola/PainelProfessores/${this.codigoEscola}/${this.ano}/CodigoPessoal/${codigoProfessor}`,
      { params: { semestre: this.semestre } },
    );
    return response.data;
  }

  async listarDisciplinasRede(): Promise<TextoValor> {
    const result = await api.get<TextoValor>(`/Escola/DisciplinasRedeEnsino/${this.ano}`);
    return result.data;
  }

  async listarSituacao(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownSituacao`);
    return result.data;
  }

  async listarDependenciaAdministrativa(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownDepAdm`);
    return result.data;
  }

  async listarCategoriaEscolaPrivada(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownCatEscPriv`);
    return result.data;
  }

  async listarRegulamentacao(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownRegulamentacao`);
    return result.data;
  }

  async listarConvenioSetorPublico(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownConvSetorPublico`);
    return result.data;
  }

  async listarEscolaridade(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownEscolaridade`);
    return result.data;
  }

  async listarTipoRegistro(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownTipoRegistro`);
    return result.data;
  }

  async listarTipoOcupacao(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownTipoOcupacaoEscola`);
    return result.data;
  }

  async listarLocalizacaoDiferenciada(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownLocalizacaoDiferenciada`);
    return result.data;
  }

  async listarLinguasIndigenas(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropdownLinguasIndigenas`);
    return result.data;
  }

  async listarIES(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/IES/DropdownList`);
    return result.data;
  }

  async DropDownList(): Promise<TextoValor[]> {
    const result = await api.get<TextoValor[]>(`/Escola/DropDownList`);
    return result.data;
  }

  /**
   * Replica o período do ano letivo informado para TODAS as escolas — o "Aplicar para todas"
   * da aba Escola. Retorna quantas escolas foram atualizadas.
   */
  async aplicarAnoLetivoParaTodas(dataInicio: string, dataTermino: string): Promise<number> {
    const result = await api.post<{ escolasAtualizadas: number }>(
      '/EtapasEscola/AnoLetivo/AplicarParaTodas',
      { dataInicio, dataTermino },
    );

    return result.data?.escolasAtualizadas ?? 0;
  }

  /**
   * Busca a logomarca de uma escola
   * @param codigoEscola Código da escola
   * @returns URL ou dados da logomarca
   */
  async getLogomarca(codigoEscola: number): Promise<FotoPerfilModel> {
    const response = await api.get<FotoPerfilModel>(`/Escola/${codigoEscola}/Logomarca`);
    return response.data;
  }

  /**
   * Atualiza a logomarca de uma escola
   * @param codigoEscola Código da escola
   * @param file Arquivo da nova logomarca
   * @returns Resultado da operação
   */
  async atualizarLogomarca(codigoEscola: number, file: File): Promise<AxiosResponse<void>> {
    try {
      const formData = new FormData();
      formData.append('file', file, `${codigoEscola}${obterExtensao(file.name) || '.jpg'}`);

      return await api.post(`/Escola/${codigoEscola}/Logomarca`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('Erro ao atualizar a logomarca:', error);
      throw error;
    }
  }

  /**
   * Quantas escolas o usuário pode ver — o mesmo conjunto do seletor da barra superior, contado
   * pelo SERVIDOR (a listagem é recortada pelo token). Pede uma página de um registro só para ler
   * o total.
   */
  async contarEscolasDoUsuario(): Promise<number> {
    const resposta = await this.dadosIndexPaginado(0, 1, []);
    return resposta.data?.totalRowCount ?? 0;
  }

  /**
   * Copia o logo GRAVADO desta escola para todas as outras escolas do usuário (resolvidas no
   * servidor pelo token). Cada escola recebe uma cópia própria; a falha de uma não para as outras.
   */
  async replicarLogomarca(codigoEscola: number): Promise<ReplicacaoLogomarcaResultado> {
    const resposta = await api.post<ReplicacaoLogomarcaResultado>(
      `/Escola/${codigoEscola}/Logomarca/Replicar`,
    );
    return resposta.data;
  }

  /**
   * Copia os cabeçalhos e rodapés GRAVADOS desta escola para as outras escolas do usuário
   * (resolvidas no servidor pelo token). Vão marcadores, não o texto desta escola: cada uma imprime
   * o nome, o endereço e o logo dela. Modelo vazio aqui não apaga o de lá.
   */
  async replicarCabecalhos(codigoEscola: number): Promise<ReplicacaoLogomarcaResultado> {
    const resposta = await api.post<ReplicacaoLogomarcaResultado>(
      `/Escola/${codigoEscola}/Cabecalhos/Replicar`,
    );
    return resposta.data;
  }
}

export interface ReplicacaoLogomarcaFalha {
  codigoEscola: number;
  nome?: string;
  motivo?: string;
}

/** Resumo da replicação. A API omite do JSON o que é nulo/vazio — por isso os opcionais. */
export interface ReplicacaoLogomarcaResultado {
  totalEscolas?: number;
  atualizadas?: number;
  falhas?: ReplicacaoLogomarcaFalha[];
}

/** Um nome que a escola já teve (tabela HistorcoNomeEscola). */
export interface HistoricoNomeEscola {
  codigo: number;
  nome: string;
  /** yyyy-MM-dd ao enviar; a API devolve a data com hora. */
  dataInicial: string | null;
  dataFinal: string | null;
  autorizacaoFuncionamento: string;
  decretoCriacao: string;
}
export default new EscolaService();
